import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  TrendingUp, 
  Users, 
  Trophy, 
  Zap, 
  Target, 
  Crown, 
  Bot,
  Star,
  ArrowRight,
  Sparkles,
  BarChart3,
  Globe,
  Radio,
  Shield
} from 'lucide-react';
import { AnimatedHeroBackground } from '@/components/AnimatedHeroBackground';
import { EnhancedHeader } from '@/components/EnhancedHeader';
import { StatsCounter } from '@/components/StatsCounter';
import { EnhancedLeaderboard } from '@/components/EnhancedLeaderboard';
import { PremiumTipCard } from '@/components/PremiumTipCard';
import { CommunityFeatures } from '@/components/CommunityFeatures';
import { TipBundles } from '@/components/TipBundles';
import { SubscriptionPlans } from '@/components/SubscriptionPlans';

import { TopMatchFixes } from '@/components/TopMatchFixes';
import { FlashSales } from '@/components/FlashSales';
import { AIMatchFeed } from '@/components/AIMatchFeed';
import { RecentWinsTicker } from '@/components/RecentWinsTicker';
import { TopTipOfDay } from '@/components/TopTipOfDay';
import { TipsterSpotlight } from '@/components/TipsterSpotlight';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { sampleStats, sampleTips } from '@/data/sampleData';

interface DashboardStats {
  total_tipsters: number;
  total_tips_today: number;
  active_users: number;
  avg_roi: number;
  top_sports: Array<{ sport: string; count: number }>;
}

export default function EnhancedIndex() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [featuredTips, setFeaturedTips] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard stats
      const { data: statsData } = await supabase.functions.invoke('get-dashboard-analytics', {
        body: user ? { user_id: user.id } : {}
      });

      if (statsData && statsData.total_tipsters > 0) {
        setStats(statsData);
      } else {
        // Use sample data if no real data or API returns empty
        setStats(sampleStats);
      }

      // Fetch featured tips
      const { data: tipsData } = await supabase
        .from('tips')
        .select(`
          *,
          tipsters!inner (
            id,
            profile_id,
            win_rate,
            roi_percentage,
            is_premium,
            profiles!inner (
              display_name,
              avatar_url
            )
          ),
          ai_tipster_personas (
            name,
            avatar_url
          )
        `)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .order('confidence', { ascending: false })
        .limit(6);

      if (tipsData && tipsData.length > 0) {
        setFeaturedTips(tipsData.map(tip => ({
          ...tip,
          tipster: {
            ...tip.tipsters,
            profile: tip.tipsters.profiles
          },
          ai_persona: tip.ai_tipster_personas
        })));
      } else {
        // Use sample tips if no real data
        setFeaturedTips(sampleTips as any);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use sample data on error
      setStats(sampleStats);
      setFeaturedTips(sampleTips as any);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <EnhancedHeader />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <AnimatedHeroBackground />
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-2">
                <Sparkles className="h-4 w-4 mr-2" />
                AI-Powered Betting Intelligence
              </Badge>
              <h1 className="text-5xl md:text-7xl font-bold">
                <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                  BetVerse
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
                Join the world's most advanced AI-powered tipster marketplace. 
                Get premium betting insights from top tipsters and AI algorithms.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90" asChild>
                <Link to="/tips">
                  <Target className="h-5 w-5 mr-2" />
                  Explore Tips
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/become-tipster">
                  <Crown className="h-5 w-5 mr-2" />
                  Become a Tipster
                </Link>
              </Button>
            </div>

            {/* Quick Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{stats.total_tipsters}+</div>
                  <div className="text-sm text-muted-foreground">Expert Tipsters</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{stats.avg_roi}%</div>
                  <div className="text-sm text-muted-foreground">Average ROI</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{stats.active_users}</div>
                  <div className="text-sm text-muted-foreground">Active Users</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{stats.total_tips_today}</div>
                  <div className="text-sm text-muted-foreground">Tips Today</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recent Wins Ticker - min-height reserves space to prevent CLS */}
      <section className="py-8 bg-gradient-to-r from-green-500/5 to-emerald-500/5 min-h-[156px]">
        <div className="container">
          <RecentWinsTicker />
        </div>
      </section>

      {/* Top Tip of the Day */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <TopTipOfDay />
        </div>
      </section>

      {/* Featured Tips + AI Match Feed */}
      <section className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-3xl font-bold mb-2">Premium Tips</h2>
                  <p className="text-muted-foreground">
                    High-confidence tips from top tipsters and AI algorithms
                  </p>
                </div>
                <Button 
                  size="lg" 
                  variant="default" 
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                  asChild
                >
                  <Link to="/live" className="flex items-center gap-2">
                    <Radio className="h-5 w-5 animate-pulse" />
                    Watch Live Games
                  </Link>
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {featuredTips.slice(0, 4).map((tip) => (
                  <PremiumTipCard key={tip.id} tip={tip} />
                ))}
              </div>

              <div className="text-center">
                <Button size="lg" variant="outline" asChild>
                  <Link to="/tips">
                    View All Tips
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <AIMatchFeed />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - AI Forecasting Explained */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <Badge className="bg-purple-500/10 text-purple-700 border-purple-500/20 mb-4">
              <Bot className="h-4 w-4 mr-2" />
              AI-Powered Sports Forecasting
            </Badge>
            <h2 className="text-3xl font-bold mb-4">How BetVerse Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our platform combines AI personas, market sentiment analysis, and verified tipster expertise to deliver world-class sports intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="p-6 text-center hover:shadow-elegant transition-shadow">
              <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI Tipster Personas</h3>
              <p className="text-muted-foreground text-sm">
                Specialized AI agents like "The Underdog Oracle" and "Market Whisperer" analyze thousands of data points in real-time using advanced machine learning models
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-elegant transition-shadow">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Market Sentiment</h3>
              <p className="text-muted-foreground text-sm">
                Track odds movement, betting volume, and community sentiment to identify value opportunities before the market corrects
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-elegant transition-shadow">
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Verified Tipsters</h3>
              <p className="text-muted-foreground text-sm">
                Access insights from professional analysts with proven track records, transparent statistics, and real-time performance monitoring
              </p>
            </Card>
          </div>

          <div className="bg-muted/30 rounded-lg p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="text-2xl font-bold mb-2">Responsible Gaming Commitment</h3>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              BetVerse is a sports intelligence platform for entertainment and educational purposes. 
              We promote responsible gaming practices and require age verification (18+). 
              Never bet more than you can afford to lose.
            </p>
          </div>
        </div>
      </section>

      {/* Tipster Spotlight */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <TipsterSpotlight />
        </div>
      </section>

      {/* Top Match Fixes & Leaderboard */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Top Performers</h2>
                <p className="text-muted-foreground">
                  See who's dominating the leaderboards this month
                </p>
              </div>
              <EnhancedLeaderboard />
              <div className="text-center mt-8">
                <Button variant="outline" asChild>
                  <Link to="/leaderboard">
                    <Trophy className="h-4 w-4 mr-2" />
                    View Full Leaderboard
                  </Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-1">
              <TopMatchFixes />
            </div>
          </div>
        </div>
      </section>

      {/* Flash Sales */}
      <section className="py-16 bg-gradient-to-b from-orange-500/5 to-red-500/5">
        <div className="container">
          <FlashSales />
        </div>
      </section>

      {/* Tip Bundles */}
      <section className="py-16">
        <div className="container">
          <TipBundles />
        </div>
      </section>

      {/* Community Features */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <CommunityFeatures />
        </div>
      </section>

      {/* Subscription Plans */}
      <section className="py-16">
        <div className="container">
          <SubscriptionPlans />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Win Big?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of successful bettors who trust BetVerse for their winning tips.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-primary to-secondary">
              <Link to="/auth" className="flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Get Started Free
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/tips" className="flex items-center">
                <Star className="h-5 w-5 mr-2" />
                Browse Tips
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t">
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">BetVerse</span>
              </div>
              <p className="text-muted-foreground">
                The world's most advanced AI-powered betting tips marketplace.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <Link to="/tips" className="block hover:text-primary transition-colors">Tips</Link>
                <Link to="/tipsters" className="block hover:text-primary transition-colors">Tipsters</Link>
                <Link to="/leaderboard" className="block hover:text-primary transition-colors">Leaderboard</Link>
                <Link to="/become-tipster" className="block hover:text-primary transition-colors">Become Tipster</Link>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <a href="#" className="block hover:text-primary transition-colors">Help Center</a>
                <a href="#" className="block hover:text-primary transition-colors">Contact Us</a>
                <a href="#" className="block hover:text-primary transition-colors">Community</a>
                <a href="#" className="block hover:text-primary transition-colors">Blog</a>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <a href="#" className="block hover:text-primary transition-colors">Terms of Service</a>
                <a href="#" className="block hover:text-primary transition-colors">Privacy Policy</a>
                <a href="#" className="block hover:text-primary transition-colors">Responsible Gaming</a>
                <a href="#" className="block hover:text-primary transition-colors">Disclaimer</a>
              </div>
            </div>
          </div>
          
          <Separator className="my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground">
            <p>&copy; 2024 BetVerse. All rights reserved.</p>
            <p>🔞 Gambling can be addictive. Please bet responsibly.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}